import { Api } from '@monorepo/shared/apiClient';
import {
  LogEntryCacheEvents,
  logEntryCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/logEntryCacheRegister';
import {
  LogFieldCacheEvents,
  logFieldCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/logFieldCacheRegister';
import {
  ResourceCacheEvents,
  resourceCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/resourceCacheRegister';
import {
  ResourcePropertyCacheEvents,
  resourcePropertyCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/resourcePropertyCacheRegister';
import {
  ResourceTypeCacheEvents,
  resourceTypeCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/resourceTypeCacheRegister';
import {
  WorkflowCacheEvents,
  workflowCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/workflowCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, usePaginatedQuery } from 'react-query';
import { OptionalKeys } from 'utility-types';

type KeyParams = Api.FetchQueryResultsParams;
type OptionalKeyParams = Pick<KeyParams, OptionalKeys<KeyParams>>;

const KEY_START = 'queryResults';

export const createQueryKey = ({
  originId,
  organizationId,
  perPage = 50,
  projectId,
  query,
  requestedPage = 0,
}: KeyParams) =>
  [
    KEY_START,
    organizationId,
    originId,
    projectId,
    query,
    { perPage, requestedPage },
  ] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createQueryKey,
  typeof Api.fetchQueryResults
>;

type UseQueryResultsParams = Partial<Api.FetchQueryResultsParams> & {
  config?: QueryConfig<Api.FetchQueryResultsResponse, Api.ErrorResponse>;
};

const fetcher: Fetcher = (
  _: string,
  organizationId: string,
  originId: string,
  projectId: string,
  query: KeyParams['query'],
  { perPage, requestedPage }: OptionalKeyParams,
) =>
  Api.fetchQueryResults({
    originId,
    organizationId,
    perPage,
    projectId,
    query,
    requestedPage,
  });

export const useQueryResults = ({
  config: inputConfig,
  originId,
  organizationId,
  perPage,
  projectId,
  query,
  requestedPage,
}: UseQueryResultsParams) => {
  const isEnabled = !!organizationId && !!originId && !!projectId && !!query;
  const config = getQueryConfig<Api.FetchQueryResultsResponse>(
    inputConfig,
    isEnabled,
  );
  const key = isEnabled
    ? createQueryKey({
        originId,
        organizationId,
        perPage,
        projectId,
        query,
        requestedPage,
      })
    : undefined;
  const { resolvedData: queryResult, ...queryInfo } = usePaginatedQuery(
    key,
    fetcher,
    config,
  );
  return {
    queryResult,
    ...queryInfo,
  };
};

type QueryCache = ReturnType<typeof useQueryResults>['queryResult'] | undefined;
type NonEmptyQueryCache = Exclude<QueryCache, undefined>;

export function getCache(keyParams: KeyParams): QueryCache {
  return queryCache.getQueryData<QueryCache>(createQueryKey(keyParams));
}

export function setCache(
  keyParams: KeyParams,
  newItems: NonEmptyQueryCache,
): void {
  queryCache.setQueryData(createQueryKey(keyParams), newItems);
}

type InvalidateCacheParams = Omit<
  KeyParams,
  keyof OptionalKeyParams | 'originId' | 'projectId' | 'query'
> & {
  originId?: string;
  projectId?: string;
};

// allow invalidating all entries lists (across projects) for a log
export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([
      KEY_START,
      keyParams.organizationId,
      keyParams.originId,
      keyParams.projectId,
    ]),
  );
}

const cacheRegisterInvalidator = {
  hookName: 'useQueryResults',
  callback: (keyParams: InvalidateCacheParams) => invalidateCache(keyParams),
};

logEntryCacheRegister(
  [
    LogEntryCacheEvents.CREATE,
    LogEntryCacheEvents.UPDATE,
    LogEntryCacheEvents.DELETE,
  ],
  cacheRegisterInvalidator,
);

logFieldCacheRegister(
  [
    LogFieldCacheEvents.CREATE,
    LogFieldCacheEvents.DELETE,
    LogFieldCacheEvents.UPDATE,
  ],
  cacheRegisterInvalidator,
);

resourceCacheRegister(
  [
    ResourceCacheEvents.CREATE,
    ResourceCacheEvents.DELETE,
    ResourceCacheEvents.UPDATE,
  ],
  cacheRegisterInvalidator,
);

resourcePropertyCacheRegister(
  [
    ResourcePropertyCacheEvents.CREATE,
    ResourcePropertyCacheEvents.DELETE,
    ResourcePropertyCacheEvents.UPDATE,
  ],
  cacheRegisterInvalidator,
);

resourceTypeCacheRegister(
  [
    ResourceTypeCacheEvents.CREATE,
    ResourceTypeCacheEvents.DELETE,
    ResourceTypeCacheEvents.UPDATE,
  ],
  cacheRegisterInvalidator,
);

workflowCacheRegister([WorkflowCacheEvents.UPDATE], cacheRegisterInvalidator);
